import { useEffect, useState } from 'react'
import { BoxTruckSenderLine, BoxTruckSenderLines } from '@numeo/types-main-web-app'
import { fetchBoxTruckSenderLines } from '../services/boxTruckLoadsApi'
import { smsFailureReason, trackBoxTruckUiInteraction } from '../utils/analytics'

/** `Dispatch · +14695550101`, or just the number when Quo has no name for the line. */
export const formatSenderLine = (line: BoxTruckSenderLine): string => `${line.name ? `${line.name} · ` : ''}${line.phoneNumber}`

/**
 * The lines this operator may send from, fetched once per mount.
 *
 * A failure answers `null` and the caller renders nothing. No toast: a tenant with no Quo connected is
 * the ordinary way for this to fail, and a banner on every inbox open is one people learn to ignore —
 * both surfaces already refuse the SEND itself with the server's own message, which is the moment that
 * matters. But it is reported where someone will see it: an absent picker is indistinguishable from
 * "you only have one line", so without a series a Quo outage looks like a quiet UI while operators
 * send believing they know which number it left on.
 */
export const useSenderLines = (enabled = true, check?: string | null): BoxTruckSenderLines | null => {
    /** The answer, with the `check` it was fetched for — an answer about line A does not answer line B. */
    const [answer, setAnswer] = useState<{ lines: BoxTruckSenderLines; checkedFor: string | null } | null>(null)
    useEffect(() => {
        // Not until the choice is actually on offer. The mailing form starts on Telegram, which has no
        // sender to pick, and fetching on mount spent a Quo read on every open of a page most of whose
        // visits never reach the picker.
        if (!enabled) return
        // Once per mount — EXCEPT when `check` names a line this answer was not about. `check` asks about
        // one stored line and only its own verdict comes back, so opening a second mailing kept the first
        // one's verdict: ignored by id, which reads as "not one of your lines" for a line Quo had
        // released. Only a new id refetches; dropping back to no check reuses what is held, because a
        // mailing that names no line has no verdict to want.
        if (answer && (!check || answer.checkedFor === check)) return
        let cancelled = false
        void (async () => {
            try {
                const lines = await fetchBoxTruckSenderLines(check)
                if (!cancelled) setAnswer({ lines, checkedFor: check ?? null })
            } catch (e) {
                console.error('[box-truck] could not load the Quo lines this operator may send from', e)
                trackBoxTruckUiInteraction('sender_lines_unavailable', smsFailureReason(e))
            }
        })()
        return () => {
            cancelled = true
        }
    }, [enabled, answer, check])
    return answer?.lines ?? null
}

/**
 * "Send from" — which of the company's Quo lines this message leaves on.
 *
 * One component because the mailing form and the SMS composer ask the same thing and must offer the
 * same answer: two copies drifted on the label alone, and the label is how an operator knows which
 * number a driver will reply to.
 *
 * Renders NOTHING when there is no choice to make. One line is a label, not a picker, and a tenant
 * whose lines could not be read gets the behaviour they had before this existed — the sender's own.
 */
export const SenderLineSelect = ({
    lines,
    value,
    stored,
    someoneElses,
    disabled,
    onChange,
}: {
    lines: BoxTruckSenderLines | null
    /** The chosen line's Quo id, or null for "whichever line is mine". */
    value: string | null
    /**
     * A line the record already names that this caller's options do not contain — an admin set it, or
     * Quo has released it since.
     *
     * Offered as an option anyway, because the alternative is worse than an odd entry: with no option
     * for it the `<select>` falls back to displaying "My line" while the save sends the stored id
     * straight back, so the form names one number and the drivers are texted from another.
     */
    stored?: { id: string; phoneNumber: string | null } | null
    /**
     * True when the empty option means someone ELSE's line — a record this reader did not create.
     *
     * Without it the option said `My line — <reader's number>` on a colleague's mailing, which is a
     * number its drivers never see: a mailing with no line of its own goes out on its CREATOR's, and
     * editing it does not change that. A flag rather than their address, because the creator's email
     * does not cross the wire — the list it comes from is not group-scoped.
     */
    someoneElses?: boolean
    disabled?: boolean
    onChange: (phoneNumberId: string | null) => void
}) => {
    // What the server says became of it, where it was asked. "Outside my groups" and "Quo released it"
    // are both "not in my list" and they send differently, so the label must not guess between them.
    const checked = stored?.id && lines?.checked?.id === stored.id ? lines.checked : null
    const orphan = stored?.id && lines && !lines.lines.some((line) => line.id === stored.id) ? stored : null
    if (!lines || (lines.lines.length < 2 && !orphan)) return null
    return (
        <div>
            <label className="text-xs font-medium text-foreground-muted" htmlFor="sender-line">
                Send from
            </label>
            <select
                id="sender-line"
                className="mt-1 w-full rounded-xl bg-surface px-3 py-2 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent"
                value={value ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value || null)}
            >
                {/* The default names the number it resolves to, so picking "mine" is not a guess — but only
                    where "mine" is what it means. On someone else's record it names them and no number,
                    which is all this page knows. */}
                <option value="">
                    {someoneElses ? "The creator's line" : lines.defaultLine ? `My line — ${formatSenderLine(lines.defaultLine)}` : 'My line'}
                </option>
                {lines.lines.map((line) => (
                    <option key={line.id} value={line.id}>
                        {formatSenderLine(line)}
                    </option>
                ))}
                {orphan && (
                    <option value={orphan.id}>
                        {checked && !checked.available
                            ? `${orphan.phoneNumber ?? 'This line'} — no longer in Quo, sends from the creator's line`
                            : orphan.phoneNumber
                              ? `${orphan.phoneNumber} — not one of your lines`
                              : 'A line you cannot choose'}
                    </option>
                )}
            </select>
            <p className="mt-1 text-[11px] text-foreground-muted">Drivers reply to this number, and their answers land in the thread it owns.</p>
        </div>
    )
}
